import React, { useMemo } from 'react';
import { useAnalytics } from '@Context/AnalyticsContext';
import { ArrowUp, ArrowDown } from '@phosphor-icons/react';

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatCurrency = (amount: number): string => `$${amount.toFixed(4)}`;

const AnalyticsSummary: React.FC = () => {
  const { summary, loading } = useAnalytics();

  const summaryCards = useMemo(
    () => [
      {
        title: 'Input Tokens',
        value: formatNumber(summary.totalInputTokens),
        icon: <ArrowDown size={24} />,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
      },
      {
        title: 'Output Tokens',
        value: formatNumber(summary.totalOutputTokens),
        icon: <ArrowUp size={24} />,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
      },
      {
        title: 'Total Tokens',
        value: formatNumber(summary.totalTokens),
        highlight: true,
      },
      {
        title: 'Input Cost',
        value: formatCurrency(summary.totalInputCost || 0),
        icon: <ArrowDown size={24} />,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
      },
      {
        title: 'Output Cost',
        value: formatCurrency(summary.totalOutputCost || 0),
        icon: <ArrowUp size={24} />,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
      },
      {
        title: 'Total Cost',
        value: formatCurrency(summary.totalCost),
        highlight: true,
      },
    ],
    [summary]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="rounded-lg bg-white p-6 shadow">
            <div className="animate-pulse">
              <div className="mb-2 flex items-center justify-between">
                <div className="h-6 w-6 rounded bg-gray-300"></div>
                <div className="h-4 w-4 rounded bg-gray-300"></div>
              </div>
              <div className="mb-1 h-8 w-16 rounded bg-gray-300"></div>
              <div className="h-4 w-20 rounded bg-gray-300"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
      {summaryCards.map((card, index) => (
        <div
          key={index}
          className="flex h-40 items-center justify-center rounded-lg bg-white p-6 shadow transition-shadow hover:shadow-md"
        >
          {card.highlight ? (
            <div className="flex h-32 w-full items-center justify-center">
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                <p className="mt-1 text-sm text-gray-600">{card.title}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center">
                <div className="flex items-center space-x-3">
                  <div
                    className={`rounded-lg p-2 ${card.bgColor} flex items-center justify-center`}
                  >
                    <div className={card.color}>{card.icon}</div>
                  </div>

                  <p className="truncate text-2xl font-bold text-gray-900">
                    {card.value}
                  </p>
                </div>

                <p className="mt-2 text-center text-sm text-gray-600">
                  {card.title}
                </p>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default AnalyticsSummary;
